import React, { useState, useEffect } from 'react';
import { Close, Email, WhatsApp, Sms, Add, Person, Search, Business } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchCustomers, createCustomer, clientTypeBracket } from '../../../services/customerService';
import { storageKeys } from '../../../config/environment';

export const CreatePaymentLinkModal = ({ show, onClose, onCreate, terminals = [], initialArticle = null, initialCustomer = null }) => {
    const { selectedOrganization, organizations } = useAuth();
    const [selectedTerminal, setSelectedTerminal] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        amount: '',
        currency: 'CDF',
        reason: '', // message/raison
        method: 'sms', // méthode de notification
        company_id: selectedOrganization?.id,
        customer: null, // Objet client complet comme sur mobile
        paymentMethod: {
            prefix: '243',
            provider_id: null,
            suffix: null,
            account: null,
            full_name: null,
            expiry_date: null
        }
    });
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [searchCustomerTerm, setSearchCustomerTerm] = useState('');
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showNewClientForm, setShowNewClientForm] = useState(false);
    const [newCustomerData, setNewCustomerData] = useState({
        name: '',
        phoneNumber: '',
        zip: '243',
        email: '',
        address: ''
    });
    const [creatingCustomer, setCreatingCustomer] = useState(false);

    useEffect(() => {
        if (show && terminals.length > 0 && !selectedTerminal) {
            setSelectedTerminal(terminals[0]);
        }
        if (show && terminals.length === 0) {
            setSelectedTerminal(null);
        }
    }, [show, terminals]);

    // Réinitialiser le formulaire quand le modal s'ouvre (préremplir si initialArticle ou initialCustomer)
    useEffect(() => {
        if (show) {
            const title = initialArticle?.name ?? '';
            const amount = initialArticle?.price != null ? String(initialArticle.price) : '';
            const customer = initialCustomer ?? (initialArticle ? null : null);
            setFormData({
                title,
                amount,
                currency: initialArticle?.currency || initialCustomer?.currency || 'CDF',
                reason: '',
                method: 'sms',
                company_id: selectedOrganization?.id,
                customer: customer || null,
                paymentMethod: {
                    prefix: customer?.zip || '243',
                    provider_id: null,
                    suffix: null,
                    account: customer?.phoneNumber || null,
                    full_name: customer?.name || null,
                    expiry_date: null
                }
            });
            setSearchCustomerTerm('');
            setShowNewClientForm(false);
        }
    }, [show, selectedOrganization, initialArticle?.id, initialCustomer?.id]);

    useEffect(() => {
        if (show && selectedOrganization?.id && showCustomerModal) {
            loadCustomers();
        }
    }, [show, selectedOrganization, showCustomerModal, searchCustomerTerm]);

    // Réinitialiser le formulaire de client quand le modal s'ouvre
    useEffect(() => {
        if (showCustomerModal) {
            console.log('[CreatePaymentLinkModal] Ouverture du modal de création de client');
            console.log('[CreatePaymentLinkModal] selectedOrganization:', selectedOrganization);
            console.log('[CreatePaymentLinkModal] organizations:', organizations);
            
            // L'organisation est déjà sélectionnée dans le contexte
            setNewCustomerData({
                name: '',
                phoneNumber: '',
                zip: '243',
                email: '',
                address: ''
            });
        }
    }, [showCustomerModal, selectedOrganization, organizations]);

    const loadCustomers = async () => {
        if (!selectedOrganization?.id) return;
        
        try {
            setLoadingCustomers(true);
            const response = await fetchCustomers({
                pge: 1,
                lmt: 100,
                slg: searchCustomerTerm || '',
                org: selectedOrganization.id
            });
            setCustomers(response.contents || []);
        } catch (error) {
            console.error('Error loading customers:', error);
            setCustomers([]);
        } finally {
            setLoadingCustomers(false);
        }
    };

    // Charger les clients avec debounce lors de la recherche
    useEffect(() => {
        if (showCustomerModal && searchCustomerTerm !== undefined) {
            const timeoutId = setTimeout(() => {
                if (selectedOrganization?.id) {
                    loadCustomers();
                }
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [searchCustomerTerm, showCustomerModal, selectedOrganization]);

    const handleCreateCustomer = async () => {
        if (!newCustomerData.name || !newCustomerData.phoneNumber) {
            alert('Veuillez remplir au moins le nom et le téléphone');
            return;
        }

        // Utiliser directement l'organisation sélectionnée (on est déjà dans une ORG)
        const orgId = selectedOrganization?.id;
        
        if (!orgId) {
            alert('Aucune organisation sélectionnée. Veuillez sélectionner une organisation d\'abord.');
            return;
        }
        
        console.log('[CreatePaymentLinkModal] Utilisation de l\'organisation:', orgId);

        try {
            setCreatingCustomer(true);
            // Construire les données du client avec company_id depuis l'organisation sélectionnée
            const customerData = {
                name: newCustomerData.name,
                phoneNumber: newCustomerData.phoneNumber,
                zip: newCustomerData.zip || '243',
                email: newCustomerData.email || '',
                address: newCustomerData.address || '',
                company_id: orgId,
                client_type: newCustomerData.client_type || 'PP',
                nif: newCustomerData.nif || null,
                docReference: newCustomerData.docReference || null,
                showInfoOnInvoice: true
            };
            
            console.log('[CreatePaymentLinkModal] Création du client avec:', customerData);
            const newCustomer = await createCustomer(customerData);
            
            // Sélectionner automatiquement le nouveau client (objet complet comme sur mobile)
            setFormData(prev => ({
                ...prev,
                customer: {
                    id: newCustomer.id || newCustomer.payload?.id,
                    name: newCustomer.name || newCustomer.payload?.name || customerData.name,
                    phoneNumber: newCustomer.phoneNumber || newCustomer.payload?.phoneNumber || customerData.phoneNumber,
                    zip: newCustomer.zip || newCustomer.payload?.zip || customerData.zip,
                    email: newCustomer.email || newCustomer.payload?.email || customerData.email,
                    address: newCustomer.address || newCustomer.payload?.address || customerData.address,
                    company_id: newCustomer.company_id || newCustomer.payload?.company_id || orgId,
                    client_type: customerData.client_type || 'PP',
                    clientType: customerData.client_type || 'PP'
                }
            }));
            
            // Recharger la liste des clients
            await loadCustomers();
            
            // Fermer le modal de création et revenir à la recherche
            setShowNewClientForm(false);
            setSearchCustomerTerm('');
            setNewCustomerData({
                name: '',
                phoneNumber: '',
                zip: '243',
                email: '',
                address: ''
            });
        } catch (error) {
            console.error('Error creating customer:', error);
            alert(error.message || 'Erreur lors de la création du client');
        } finally {
            setCreatingCustomer(false);
        }
    };

    const handleSelectCustomer = (customer) => {
        // Stocker l'objet client complet + type (backend)
        setFormData(prev => ({
            ...prev,
            customer: {
                id: customer.id,
                name: customer.name,
                phoneNumber: customer.phoneNumber,
                zip: customer.zip || '243',
                email: customer.email || '',
                address: customer.address || '',
                company_id: customer.company_id || selectedOrganization?.id,
                client_type: customer.client_type || customer.clientType || 'PP',
                clientType: customer.clientType || customer.client_type || 'PP'
            }
        }));
        setSearchCustomerTerm('');
        setShowCustomerModal(false);
    };

    const notificationOptions = [
        {
            id: 'whatsapp',
            icon: WhatsApp,
            label: 'WhatsApp',
            description: 'Envoi via WhatsApp Business',
            color: 'text-emerald-600',
            bgColor: 'bg-emerald-50',
            borderColor: 'border-emerald-200'
        },
        {
            id: 'email',
            icon: Email,
            label: 'Email',
            description: 'Envoi par courriel',
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200'
        },
        {
            id: 'sms',
            icon: Sms,
            label: 'SMS',
            description: 'Envoi par message texte',
            color: 'text-amber-600',
            bgColor: 'bg-amber-50',
            borderColor: 'border-amber-200'
        }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation comme sur mobile / backend
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            alert('Le montant est vide ou inférieur à zéro');
            return;
        }

        if (formData.method !== 'flash' && !formData.customer) {
            alert('Veuillez sélectionner un client');
            return;
        }
        // Validation selon la méthode d'envoi (le backend enverra par ce canal)
        if (formData.method === 'email' && formData.customer && (!formData.customer.email || !String(formData.customer.email).trim())) {
            alert('Pour un envoi par email, le client doit avoir une adresse email');
            return;
        }
        if ((formData.method === 'sms' || formData.method === 'whatsapp') && formData.customer && (!formData.customer.phoneNumber || !String(formData.customer.phoneNumber).trim())) {
            alert('Pour un envoi par SMS ou WhatsApp, le client doit avoir un numéro de téléphone');
            return;
        }
        if (formData.method === 'email' && (!formData.reason || !String(formData.reason).trim())) {
            alert('Pour un envoi par email, le message (motif) est obligatoire');
            return;
        }
        if (!selectedTerminal?.id) {
            alert('Veuillez sélectionner un terminal');
            return;
        }

        try {
            const pm = formData.paymentMethod || {};
            const paymentLinkData = {
                terminal_id: selectedTerminal.id,
                terminal: selectedTerminal.id,
                title: formData.title,
                reason: formData.reason,
                amount: parseFloat(formData.amount),
                currency: formData.currency,
                company_id: formData.company_id || selectedOrganization?.id,
                method: formData.method,
                customer: formData.customer || {
                    zip: '243',
                    phoneNumber: '',
                    email: '',
                    name: '',
                    address: '',
                    company_id: selectedOrganization?.id
                },
                paymentMethod: {
                    prefix: pm.prefix ?? '243',
                    provider_id: pm.provider_id ?? null,
                    provider_name: pm.provider_name ?? null,
                    suffix: pm.suffix ?? null,
                    account: pm.account ?? null,
                    full_name: pm.full_name ?? null,
                    expiration_date: pm.expiration_date ?? pm.expiry_date ?? null,
                    control: pm.control ?? null,
                    owner: pm.owner ?? null,
                }
            };

            await onCreate(paymentLinkData);
            
            // Réinitialiser le formulaire seulement si la création réussit
            setFormData({
                title: '',
                amount: '',
                currency: 'CDF',
                reason: '',
                method: 'sms',
                company_id: selectedOrganization?.id,
                customer: null,
                paymentMethod: {
                    prefix: '243',
                    provider_id: null,
                    suffix: null,
                    account: null,
                    full_name: null,
                    expiry_date: null
                }
            });
        } catch (error) {
            console.error('Error in handleSubmit:', error);
        }
    };

    const handleNotificationSelect = (method) => {
        // Sélection unique comme sur mobile (switch)
        setFormData(prev => ({
            ...prev,
            method: method
        }));
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 border border-blue-200/70 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Nouveau Lien de Paiement</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <Close className="text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Titre */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Titre du lien *
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            placeholder="Ex: Paiement Abonnement Mensuel"
                            className="w-full px-4 py-3 bg-white/50 border-2 border-blue-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                        />
                    </div>

                    {/* Terminal */}
                    {terminals.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Terminal *
                            </label>
                            <select
                                value={selectedTerminal?.id || ''}
                                onChange={(e) => {
                                    const t = terminals.find(t => t.id === e.target.value);
                                    setSelectedTerminal(t || null);
                                }}
                                className="w-full px-4 py-3 bg-white/50 border-2 border-blue-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                            >
                                {terminals.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name || t.tag || t.id}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Montant et Devise */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Montant *
                            </label>
                            <input
                                type="number"
                                required
                                min="0.01"
                                step="0.01"
                                value={formData.amount}
                                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                                placeholder="0.00"
                                className="w-full px-4 py-3 bg-white/50 border-2 border-blue-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Devise *
                            </label>
                            <select
                                value={formData.currency}
                                onChange={(e) => setFormData({...formData, currency: e.target.value})}
                                className="w-full px-4 py-3 bg-white/50 border-2 border-blue-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                            >
                                <option value="CDF">CDF (FC)</option>
                                <option value="USD">USD ($)</option>
                            </select>
                        </div>
                    </div>

                    {/* Client - Champ en lecture seule qui ouvre le modal (comme sur mobile) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client {formData.method === 'flash' ? <span className="text-gray-500 font-normal">(Optionnel)</span> : <span className="text-red-500">*</span>}
                        </label>
                        <div
                            onClick={() => setShowCustomerModal(true)}
                            className="w-full px-4 py-3 bg-white/50 border-2 border-blue-200/50 rounded-xl cursor-pointer hover:border-blue-400 transition-all flex items-center justify-between"
                        >
                            <span className={formData.customer ? 'text-gray-900' : 'text-gray-400'}>
                                {formData.customer ? (
                                    <>
                                        {formData.customer.name}
                                        <span className="text-gray-500 font-medium tabular-nums ml-1.5">
                                            {clientTypeBracket(formData.customer)}
                                        </span>
                                    </>
                                ) : (
                                    'Nom, email ou téléphone'
                                )}
                            </span>
                            <Person className="text-gray-400" />
                        </div>
                        {formData.customer && (
                            <div className="mt-2 text-sm text-gray-600">
                                <p>Téléphone: {formData.customer.zip}{formData.customer.phoneNumber}</p>
                                {formData.customer.email && <p>Email: {formData.customer.email}</p>}
                            </div>
                        )}
                    </div>

                    {/* Message (reason) - obligatoire pour envoi par email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Message {formData.method === 'email' ? <span className="text-red-500">*</span> : '(Optionnel)'}
                        </label>
                        <textarea
                            value={formData.reason}
                            onChange={(e) => setFormData(prev => ({...prev, reason: e.target.value}))}
                            placeholder="Note pour le client"
                            rows="3"
                            className="w-full px-4 py-3 bg-white/50 border-2 border-blue-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all resize-none"
                        />
                    </div>

                    {/* Méthode de notification - Switch comme sur mobile */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Méthode de notification
                        </label>
                        <div className="space-y-3">
                            {notificationOptions.map((option) => (
                                <div key={option.id} className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-200">
                                    <div className="flex items-center space-x-3">
                                        <option.icon className={`text-xl ${option.color}`} />
                                        <span className="font-medium text-gray-900">{option.label}</span>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.method === option.id}
                                            onChange={() => handleNotificationSelect(option.id)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Informations : envoi au client */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-center space-x-2 text-blue-700">
                            <span className="text-lg">💡</span>
                            <div className="text-sm">
                                <p className="font-medium">Le lien sera envoyé au client par {formData.method === 'email' ? 'email' : formData.method === 'whatsapp' ? 'WhatsApp' : 'SMS'}</p>
                                <p className="text-blue-600">Le lien expirera automatiquement (valable jusqu'au {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')})</p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-4 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-4 bg-gray-500/10 text-gray-700 rounded-xl hover:bg-gray-500/20 transition-all duration-300 border border-gray-300/50 font-semibold"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-lg transform hover:-translate-y-0.5"
                        >
                            Créer et envoyer le lien
                        </button>
                    </div>
                </form>

                {/* Modal de sélection/création de client (comme sur mobile) */}
                {showCustomerModal && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold text-gray-900">Client</h3>
                                <button
                                    onClick={() => {
                                        setShowCustomerModal(false);
                                        setShowNewClientForm(false);
                                        setSearchCustomerTerm('');
                                        setNewCustomerData({
                                            name: '',
                                            phoneNumber: '',
                                            zip: '243',
                                            email: '',
                                            address: ''
                                        });
                                    }}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <Close className="text-gray-500" />
                                </button>
                            </div>
                            <div className="border-b border-gray-200 mb-4"></div>

                            {!showNewClientForm ? (
                                <>
                                    {/* Recherche de client */}
                                    <div className="flex space-x-2 mb-4">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                value={searchCustomerTerm}
                                                onChange={(e) => setSearchCustomerTerm(e.target.value)}
                                                placeholder="Nom, Email, Telephone,..."
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400"
                                            />
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowNewClientForm(true);
                                            }}
                                            className="px-4 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-all"
                                            title="Nouveau client"
                                        >
                                            <Add />
                                        </button>
                                    </div>

                                    {/* Liste des clients */}
                                    <div className="max-h-96 overflow-y-auto">
                                        {loadingCustomers ? (
                                            <div className="text-center py-8">
                                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                                                <p className="mt-2 text-gray-600">Chargement...</p>
                                            </div>
                                        ) : customers.length > 0 ? (
                                            <div className="space-y-2">
                                                {customers.map((customer) => (
                                                    <div
                                                        key={customer.id}
                                                        onClick={() => handleSelectCustomer(customer)}
                                                        className="p-3 border-b border-gray-200 hover:bg-gray-50 cursor-pointer rounded-lg transition-colors"
                                                    >
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
                                                                {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="font-medium text-gray-900">
                                                                    {customer.name}{' '}
                                                                    <span className="text-gray-500 font-medium tabular-nums">
                                                                        {clientTypeBracket(customer)}
                                                                    </span>
                                                                </p>
                                                                <p className="text-sm text-gray-600">{customer.phoneNumber}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-gray-500">
                                                Aucun(e) client(e) trouvé(e)
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Formulaire de création de client */}
                                    <div className="space-y-4">
                                        {/* Organisation retirée - on est déjà dans une ORG, pas besoin d'afficher */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Nom du client</label>
                                            <input
                                                type="text"
                                                value={newCustomerData.name}
                                                onChange={(e) => setNewCustomerData(prev => ({...prev, name: e.target.value}))}
                                                placeholder="Nom du client"
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400"
                                            />
                                        </div>

                                        <div className="flex space-x-2">
                                            <div className="w-1/6">
                                                <input
                                                    type="text"
                                                    value={newCustomerData.zip}
                                                    onChange={(e) => setNewCustomerData(prev => ({...prev, zip: e.target.value}))}
                                                    placeholder="ZIP"
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    type="tel"
                                                    value={newCustomerData.phoneNumber}
                                                    onChange={(e) => setNewCustomerData(prev => ({...prev, phoneNumber: e.target.value}))}
                                                    placeholder="970000000"
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <input
                                                type="email"
                                                value={newCustomerData.email}
                                                onChange={(e) => setNewCustomerData(prev => ({...prev, email: e.target.value}))}
                                                placeholder="Email"
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400"
                                            />
                                        </div>

                                        <div>
                                            <textarea
                                                value={newCustomerData.address}
                                                onChange={(e) => setNewCustomerData(prev => ({...prev, address: e.target.value}))}
                                                placeholder="Adresse"
                                                rows="2"
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400"
                                            />
                                        </div>

                                        <div className="flex space-x-3 pt-4">
                                            <button
                                                type="button"
                                                onClick={handleCreateCustomer}
                                                disabled={creatingCustomer || !selectedOrganization}
                                                className="flex-1 bg-amber-500 text-white py-2.5 rounded-xl font-semibold hover:bg-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {creatingCustomer ? 'Enregistrement...' : 'Enregistrer'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowNewClientForm(false);
                                                    setNewCustomerData({
                                                        name: '',
                                                        phoneNumber: '',
                                                        zip: '243',
                                                        email: '',
                                                        address: ''
                                                    });
                                                }}
                                                disabled={creatingCustomer}
                                                className="px-6 py-2.5 bg-gray-500 text-white rounded-xl font-medium hover:bg-gray-600 transition-all disabled:opacity-50"
                                            >
                                                Annuler
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};